import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { LoginCredentials } from '@/types'

import { useErrorHandler } from './useErrorHandler'

interface UseLoginState {
  isLoading: boolean
  error: Nullable<string>
}

interface UseLoginReturn extends UseLoginState {
  login: (credentials: LoginCredentials) => Promise<void>
  reset: () => void
}

/**
 * Custom hook for managing login state
 * Handles login API calls and state management using AuthContext
 */
export function useLogin(): UseLoginReturn {
  const { login: authLogin } = useAuth()
  const { handleError } = useErrorHandler()
  const [state, setState] = useState<UseLoginState>({
    isLoading: false,
    error: null,
  })

  const login = async (credentials: LoginCredentials): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
    try {
      await authLogin(credentials)
      setState(prev => ({ ...prev, error: null }))
    } catch (error) {
      const errorMessage = handleError(error)
      setState(prev => ({ ...prev, error: errorMessage }))
      throw error
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }

  const reset = () => {
    setState({ isLoading: false, error: null })
  }

  return {
    ...state,
    login,
    reset,
  }
}
